import React from "react";
import { Link, useLocation } from "react-router-dom";
import { PublicHeader, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Shield, Zap, Mic, Camera, ListChecks, Bot, FileText,
  Clock, FolderOpen, Users, CheckCircle2, ClipboardCheck,
  BookOpen, Lock, ArrowRight, Sparkles,
} from "lucide-react";

const IMG_HERO = "https://images.unsplash.com/photo-1622127739239-1905bbaa21b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjB3b3JrZXIlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzg0MjQ3NDc4fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const location = useLocation();
  const refParam = (new URLSearchParams(location.search).get("ref") || "").trim().toUpperCase();
  const isReferred = /^FMG-[A-Z0-9]{4,12}$/.test(refParam);
  return (
    <div>
      <PublicHeader />

      {/* Advocate referral introduction (only with a valid ?ref= parameter) */}
      {isReferred && (
        <section className="bg-red-50 border-b border-red-100" data-testid="referral-intro-panel">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-red-700 uppercase">
                <Users className="h-4 w-4"/>You were referred to FileMyGrievance
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-900">A fellow UPSer shared FileMyGrievance with you.</p>
            </div>
          </div>
        </section>
      )}

      {/* ============ 1. HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50 border-b border-slate-200" data-testid="section-hero">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <img src="/brand/fmg-icon.png" alt="FileMyGrievance app icon" className="hidden sm:block h-14 w-14 rounded-xl object-cover shadow-sm" data-testid="hero-app-icon"/>
              <span className="inline-block text-xs font-semibold tracking-widest text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1 uppercase">Built for UPS Teamsters</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-display text-slate-900 tracking-tight leading-[1.05]" data-testid="hero-headline">
              Turn workplace problems into <span className="text-red-600">organized grievances</span>.
            </h1>
            <p className="mt-4 text-base sm:text-lg font-semibold text-slate-800 italic" data-testid="hero-supporting">
              The strongest grievance begins before you file it.
            </p>
            <p className="mt-6 text-lg text-slate-700 max-w-xl leading-relaxed" data-testid="hero-subhead">
              Don't rely on memory. Document what happened while it's fresh. Record incidents, preserve important evidence, and build a complete workplace history before important details are forgotten. When you're ready, FileMyGrievance helps you prepare a professional grievance.
            </p>
            <p className="mt-4 text-sm text-slate-600 max-w-xl italic" data-testid="hero-attribution">
              Created by a retired UPS feeder driver who experienced firsthand how quickly important workplace details can be forgotten.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register"><Button data-testid="hero-start-btn" size="lg" className="bg-red-600 hover:bg-red-700 text-white h-12 px-6"><Zap className="h-5 w-5 mr-2"/>Start a Grievance</Button></Link>
              <Link to="/how-it-works"><Button data-testid="hero-how-btn" size="lg" variant="outline" className="h-12 px-6 border-slate-300">How It Works</Button></Link>
            </div>
            <p className="mt-4 text-sm text-slate-700 max-w-xl" data-testid="hero-free-note">
              Your Workplace Timeline is always free. Upgrade only when you need additional grievance tools.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600" data-testid="hero-trust">
              <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-slate-500"/>Private &amp; Secure</span>
              <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-red-600"/>Built for UPS Teamsters</span>
              <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-red-600"/>Powered by the Official 2023–2028 UPS National Master Agreement</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-slate-200 shadow-md">
              <img src={IMG_HERO} alt="Warehouse worker documenting" className="w-full h-72 lg:h-96 object-cover"/>
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-md border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-red-700 uppercase"><Mic className="h-3.5 w-3.5"/>Incident recorded · Now</div>
                <div className="mt-1 text-sm text-slate-900 font-medium">"Supervisor loading trailers again — Local 41 grievance."</div>
                <div className="mt-1 text-xs text-slate-500">Auto-timestamped · Voice note · 2 photos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 2. WHY IT EXISTS (concise) ============ */}
      <section className="py-16 lg:py-20 bg-white border-b border-slate-200" data-testid="section-why">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-semibold tracking-widest text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1 uppercase">Why FileMyGrievance Exists</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black font-display text-slate-900">Documentation wins grievances. Memory doesn't.</h2>
            <p className="mt-4 text-lg text-slate-700 leading-relaxed">
              Too many employees leave work with only their memory, handwritten notes, or a grievance form. By the time they need to prove what happened, important dates, conversations, witnesses, and other details may be much harder to remember.
            </p>
            <p className="mt-4 text-lg text-slate-700 leading-relaxed">
              FileMyGrievance helps employees document facts while they are fresh, organize evidence, preserve workplace history, and arrive better prepared for steward review.
            </p>
          </div>

          <div className="mt-10 bg-red-50 border-l-4 border-red-600 rounded-md p-6" data-testid="employer-records-callout">
            <h3 className="text-2xl sm:text-3xl font-black font-display text-slate-900 leading-tight">
              Your employer keeps records.<br/>Shouldn't you?
            </h3>
            <p className="mt-3 text-slate-700 leading-relaxed">
              The company keeps records and builds a history. Employees should be able to build and preserve their own workplace history too.
            </p>
          </div>

          <p className="mt-6 text-sm text-slate-600 italic max-w-3xl" data-testid="steward-disclaimer">
            FileMyGrievance supports employees and stewards. It does not replace union representation or professional legal advice.
          </p>
        </div>
      </section>

      {/* ============ 3. JUST LEFT THE OFFICE (signature feature) ============ */}
      <section className="py-16 lg:py-20 bg-slate-50 border-b border-slate-200" data-testid="section-incident">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block text-xs font-semibold tracking-widest text-red-700 bg-red-50 border border-red-100 rounded-full px-3 py-1 uppercase">Signature Feature</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black font-display text-slate-900 leading-tight">Just Left the Office?</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              One tap. Voice-to-text, timestamp, and evidence — captured before you leave the parking lot. Turn it into a full grievance whenever you're ready.
            </p>
            <Link to="/register" className="mt-6 inline-block">
              <Button data-testid="incident-cta-btn" size="lg" className="bg-red-600 hover:bg-red-700 text-white h-12 px-6"><Zap className="h-5 w-5 mr-2"/>Start Recording</Button>
            </Link>
            <p className="mt-3 text-sm text-slate-500">Unlimited incident recording is included with every account.</p>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="incident-cards">
            {[
              {icon: Clock, title:"Automatic Timestamp"},
              {icon: Mic, title:"Voice Recording"},
              {icon: Camera, title:"Photos & Documents"},
              {icon: ListChecks, title:"Personal Timeline"},
              {icon: FileText, title:"Convert to Grievance"},
              {icon: Lock, title:"Secure Storage"},
            ].map((c,i)=>(
              <div key={i} className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-md bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0"><c.icon className="h-4 w-4 text-red-600"/></div>
                <span className="text-sm font-semibold text-slate-900 leading-snug">{c.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4. WHY WORKERS KEEP USING IT ============ */}
      <section className="py-16 lg:py-20 bg-slate-900 text-white" data-testid="section-benefits">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-semibold tracking-widest text-red-400 bg-red-500/10 border border-red-500/30 rounded-full px-3 py-1 uppercase">Why Workers Keep Using It</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black font-display">When no one else saw it, your documentation matters most.</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="benefit-cards">
            {[
              {icon: ClipboardCheck, title:"Never lose important details"},
              {icon: FileText, title:"Professional documentation"},
              {icon: Lock, title:"Private & Secure"},
              {icon: FolderOpen, title:"Evidence Management"},
              {icon: ListChecks, title:"Incident Timeline"},
              {icon: Zap, title:"Ready When You Need It"},
            ].map((b,i)=>(
              <div key={i} className="bg-slate-800/70 border border-slate-700 rounded-md p-5" data-testid={`benefit-${i}`}>
                <b.icon className="h-6 w-6 text-red-400"/>
                <h3 className="mt-3 font-bold font-display text-white text-base leading-snug">{b.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 5. GROWING CONTRACT COVERAGE ============ */}
      <section className="py-16 lg:py-20 bg-slate-50 border-b border-slate-200" data-testid="section-coverage">
        <div className="max-w-5xl mx-auto px-6">
          <span className="inline-block text-xs font-semibold tracking-widest text-slate-600 bg-white border border-slate-300 rounded-full px-3 py-1 uppercase">Growing Contract Coverage</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-black font-display text-slate-900">New contract coverage added regularly.</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl">
            FileMyGrievance continues expanding contract coverage to help more UPS Teamsters identify relevant contract language and prepare stronger grievances.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coverage-cards">
            {[
              {
                key: "available",
                icon: CheckCircle2,
                iconClass: "bg-green-50 border-green-200 text-green-600",
                title: "CURRENTLY AVAILABLE",
                items: [
                  "UPS National Master Agreement (2023–2028)",
                  "Central Region Supplemental Agreement",
                  "Digital Steward",
                  "Professional Grievance Builder",
                ],
                bulletTone: "check",
                badgeText: "Available Now",
                badgeClass: "bg-green-100 text-green-800 border-green-200",
              },
              {
                key: "recent",
                icon: Sparkles,
                iconClass: "bg-blue-50 border-blue-200 text-blue-600",
                title: "RECENTLY ADDED",
                items: [
                  "Southern Region Supplemental Agreement",
                  "Western Region Supplemental Agreement",
                  "Atlantic Area Supplemental Agreement",
                  "AI Contract Library Expanded",
                ],
                bulletTone: "check",
                badgeText: "Latest Update",
                badgeClass: "bg-blue-100 text-blue-800 border-blue-200",
              },
              {
                key: "next",
                icon: Clock,
                iconClass: "bg-amber-50 border-amber-200 text-amber-600",
                title: "COMING NEXT",
                items: [
                  "Additional UPS Regional Supplements",
                  "Local Riders",
                  "Letters of Understanding",
                  "Continued Contract Expansion",
                ],
                bulletTone: "dot",
                badgeText: "In Progress",
                badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
              },
              {
                key: "commitment",
                icon: Shield,
                iconClass: "bg-red-50 border-red-200 text-red-600",
                title: "OUR COMMITMENT",
                body: "We continue adding contract coverage so more UPS Teamsters receive agreement-specific guidance, helping every member prepare stronger, better-documented grievances.",
                badgeText: "Continuous Improvement",
                badgeClass: "bg-red-100 text-red-800 border-red-200",
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.key}
                  className="bg-white border border-slate-200 rounded-md p-6 flex flex-col hover:shadow-md hover:border-slate-300 transition-shadow"
                  data-testid={`coverage-card-${card.key}`}
                >
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center border ${card.iconClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-sm font-bold tracking-wider text-slate-900 uppercase">{card.title}</h3>
                  {card.items ? (
                    <ul className="mt-3 space-y-2 flex-1" data-testid={`coverage-card-${card.key}-items`}>
                      {card.items.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
                          {card.bulletTone === "check" ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          ) : (
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                          )}
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1" data-testid={`coverage-card-${card.key}-body`}>
                      {card.body}
                    </p>
                  )}
                  <div className="mt-5">
                    <span
                      className={`inline-block text-[11px] font-semibold tracking-widest uppercase border rounded-full px-3 py-1 ${card.badgeClass}`}
                      data-testid={`coverage-card-${card.key}-badge`}
                    >
                      {card.badgeText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 7. FINAL CTA ============ */}
      <section className="py-16 lg:py-20 bg-red-600 text-white" data-testid="section-final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles className="mx-auto h-8 w-8"/>
          <h2 className="mt-4 text-3xl sm:text-4xl font-black font-display">Protect your workplace history before it's forgotten.</h2>
          <p className="mt-4 text-base sm:text-lg text-red-50" data-testid="final-free-note">
            Your Workplace Timeline is always free. Upgrade only when you need more grievance tools.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><Button data-testid="cta-final-btn" size="lg" className="bg-white text-red-700 hover:bg-slate-100 h-12 px-8">Start Recording</Button></Link>
            <Link to="/pricing"><Button data-testid="cta-plans-btn" size="lg" variant="outline" className="border-white text-white hover:bg-red-700 h-12 px-8">View Plans</Button></Link>
          </div>
        </div>
      </section>

      <div className="bg-slate-100 border-t border-b border-slate-200 py-6 text-center text-sm text-slate-600 px-4">
        <Shield className="h-4 w-4 inline mr-2 text-slate-500"/>
        FileMyGrievance is independent and is not affiliated with or endorsed by UPS, the International Brotherhood of Teamsters, or any Teamsters Local Union.
      </div>
      <Footer/>
    </div>
  );
}
